import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { fetchSecurityAlerts, getAlertStats, updateAlertStatus, subscribeToAlerts, unsubscribeFromAlerts, bulkAcknowledgeAlerts } from '../../../services/alertNotificationService';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns';

const SecurityAlertsPanel = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [filters, setFilters] = useState({
    status: 'new',
    priority: '',
    alertType: '',
    limit: 50
  });
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolvingAlert, setResolvingAlert] = useState(null);

  useEffect(() => {
    loadAlerts();
    loadStats();

    // Subscribe to real-time alerts
    const subscription = subscribeToAlerts((newAlert) => {
      // Add new alert to the list
      setAlerts(prev => [newAlert, ...prev]);
      // Show notification
      if (Notification?.permission === 'granted') {
        new Notification('Neue Sicherheitswarnung', {
          body: newAlert?.title,
          icon: '/favicon.ico'
        });
      }
    });

    // Request notification permission
    if (Notification?.permission === 'default') {
      Notification?.requestPermission();
    }

    return () => {
      unsubscribeFromAlerts(subscription);
    };
  }, [filters]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const { data, error } = await fetchSecurityAlerts(filters);
      if (error) {
        console.error('Error loading alerts:', error);
      } else {
        setAlerts(data || []);
      }
    } catch (error) {
      console.error('Exception loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error } = await getAlertStats();
      if (!error) {
        setStats(data);
      }
    } catch (error) {
      console.error('Exception loading stats:', error);
    }
  };

  const handleStatusChange = async (alertId, newStatus) => {
    try {
      const { error } = await updateAlertStatus(alertId, {
        status: newStatus,
        userEmail: user?.email || 'admin@gridbalance.de',
        resolutionNotes: newStatus === 'resolved' ? resolutionNotes : null
      });

      if (!error) {
        await loadAlerts();
        await loadStats();
        setResolvingAlert(null);
        setResolutionNotes('');
      }
    } catch (error) {
      console.error('Error updating alert status:', error);
    }
  };

  const handleBulkAcknowledge = async () => {
    try {
      const { error } = await bulkAcknowledgeAlerts(
        selectedAlerts,
        user?.email || 'admin@gridbalance.de'
      );

      if (!error) {
        await loadAlerts();
        await loadStats();
        setSelectedAlerts([]);
      }
    } catch (error) {
      console.error('Error bulk acknowledging alerts:', error);
    }
  };

  const handleSelectAlert = (alertId) => {
    setSelectedAlerts(prev =>
      prev?.includes(alertId)
        ? prev?.filter(id => id !== alertId)
        : [...prev, alertId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAlerts?.length === alerts?.length) {
      setSelectedAlerts([]);
    } else {
      setSelectedAlerts(alerts?.map(alert => alert?.id));
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case 'security_breach':
        return 'ShieldAlert';
      case 'suspicious_activity':
        return 'AlertTriangle';
      case 'compliance_violation':
        return 'AlertOctagon';
      case 'duplicate_registration':
        return 'Copy';
      case 'unauthorized_access':
        return 'Lock';
      case 'data_anomaly':
        return 'TrendingUp';
      default:
        return 'Bell';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Neu' },
      acknowledged: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Bestätigt' },
      investigating: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'In Prüfung' },
      resolved: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Gelöst' },
      dismissed: { color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Verworfen' }
    };
    const badge = badges?.[status] || badges?.new;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border ${badge?.color}`}>
        {badge?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-card border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="ShieldAlert" size={24} />
              Sicherheitswarnungen
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Echtzeit-Überwachung von Sicherheitsereignissen und Compliance-Verstößen
            </p>
          </div>
          {selectedAlerts?.length > 0 && (
            <Button
              variant="default"
              size="sm"
              iconName="Check"
              onClick={handleBulkAcknowledge}
            >
              {selectedAlerts?.length} Bestätigen
            </Button>
          )}
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <p className="text-xs text-red-600 font-medium">Kritisch</p>
              <p className="text-2xl font-bold text-red-700">{stats?.byPriority?.critical || 0}</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
              <p className="text-xs text-orange-600 font-medium">Hoch</p>
              <p className="text-2xl font-bold text-orange-700">{stats?.byPriority?.high || 0}</p>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-600 font-medium">Mittel</p>
              <p className="text-2xl font-bold text-yellow-700">{stats?.byPriority?.medium || 0}</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-600 font-medium">Niedrig</p>
              <p className="text-2xl font-bold text-blue-700">{stats?.byPriority?.low || 0}</p>
            </div>
            <div className="bg-muted p-3 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground font-medium">Ungelöst</p>
              <p className="text-2xl font-bold text-foreground">{stats?.unresolvedCount || 0}</p>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-border bg-muted/10">
        <div className="flex flex-wrap gap-3">
          <select
            value={filters?.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
          >
            <option value="">Alle Status</option>
            <option value="new">Neu</option>
            <option value="acknowledged">Bestätigt</option>
            <option value="investigating">In Prüfung</option>
            <option value="resolved">Gelöst</option>
            <option value="dismissed">Verworfen</option>
          </select>

          <select
            value={filters?.priority}
            onChange={(e) => setFilters(prev => ({ ...prev, priority: e?.target?.value }))}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
          >
            <option value="">Alle Prioritäten</option>
            <option value="critical">Kritisch</option>
            <option value="high">Hoch</option>
            <option value="medium">Mittel</option>
            <option value="low">Niedrig</option>
          </select>

          <select
            value={filters?.alertType}
            onChange={(e) => setFilters(prev => ({ ...prev, alertType: e?.target?.value }))}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
          >
            <option value="">Alle Typen</option>
            <option value="security_breach">Sicherheitsverletzung</option>
            <option value="suspicious_activity">Verdächtige Aktivität</option>
            <option value="compliance_violation">Compliance-Verstoß</option>
            <option value="duplicate_registration">Doppelte Registrierung</option>
            <option value="unauthorized_access">Unbefugter Zugriff</option>
          </select>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters({ status: 'new', priority: '', alertType: '', limit: 50 })}
          >
            <Icon name="RotateCcw" size={16} className="mr-2" />
            Zurücksetzen
          </Button>
        </div>
      </div>

      {/* Alerts List */}
      <div className="p-6 max-h-[600px] overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : alerts?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="ShieldCheck" size={48} className="mx-auto text-green-500 mb-4" />
            <p className="text-foreground font-medium">Keine Sicherheitswarnungen</p>
            <p className="text-sm text-muted-foreground mt-1">Alle Systeme funktionieren normal</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts?.map((alert) => (
              <div
                key={alert?.id}
                className={`border rounded-lg p-4 transition-all ${
                  selectedAlerts?.includes(alert?.id)
                    ? 'border-primary bg-primary/5' : 'border-border bg-card hover:shadow-md'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedAlerts?.includes(alert?.id)}
                    onChange={() => handleSelectAlert(alert?.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon name={getAlertIcon(alert?.alertType)} size={20} className="text-primary" />
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(alert?.priority)}`}>
                          {alert?.priority?.toUpperCase()}
                        </span>
                        {getStatusBadge(alert?.alertStatus)}
                        <span className="text-xs text-muted-foreground">
                          {formatDate(alert?.createdAt)}
                        </span>
                      </div>
                      <button
                        onClick={() => setExpandedAlert(expandedAlert === alert?.id ? null : alert?.id)}
                        className="flex-shrink-0"
                      >
                        <Icon
                          name={expandedAlert === alert?.id ? 'ChevronUp' : 'ChevronDown'}
                          size={20}
                          className="text-muted-foreground"
                        />
                      </button>
                    </div>

                    <h3 className="text-sm font-semibold text-foreground mt-2">{alert?.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{alert?.description}</p>

                    {/* Expanded Details */}
                    {expandedAlert === alert?.id && (
                      <div className="mt-4 pt-4 border-t border-border space-y-3">
                        {alert?.recommendedAction && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs font-medium text-blue-700 mb-1">Empfohlene Maßnahme:</p>
                            <p className="text-sm text-blue-900">{alert?.recommendedAction}</p>
                          </div>
                        )}

                        {alert?.affectedData && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Betroffene Daten:</p>
                            <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                              {JSON.stringify(alert?.affectedData, null, 2)}
                            </pre>
                          </div>
                        )}

                        {alert?.dealershipName && (
                          <p className="text-xs text-muted-foreground">
                            Partner: <span className="text-foreground font-medium">{alert?.dealershipName}</span>
                          </p>
                        )}

                        {alert?.applicationCustomerName && (
                          <p className="text-xs text-muted-foreground">
                            Kunde: <span className="text-foreground font-medium">{alert?.applicationCustomerName}</span>
                          </p>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-2">
                          {alert?.alertStatus === 'new' && (
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Check"
                              onClick={() => handleStatusChange(alert?.id, 'acknowledged')}
                            >
                              Bestätigen
                            </Button>
                          )}
                          {(alert?.alertStatus === 'new' || alert?.alertStatus === 'acknowledged') && (
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Search"
                              onClick={() => handleStatusChange(alert?.id, 'investigating')}
                            >
                              Untersuchen
                            </Button>
                          )}
                          {alert?.alertStatus !== 'resolved' && alert?.alertStatus !== 'dismissed' && (
                            <>
                              <Button
                                variant="default"
                                size="sm"
                                iconName="CheckCircle"
                                onClick={() => setResolvingAlert(alert?.id)}
                              >
                                Lösen
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="X"
                                onClick={() => handleStatusChange(alert?.id, 'dismissed')}
                              >
                                Verwerfen
                              </Button>
                            </>
                          )}
                        </div>

                        {/* Resolution Notes Input */}
                        {resolvingAlert === alert?.id && (
                          <div className="mt-3 p-3 bg-muted rounded-lg">
                            <label className="text-xs font-medium text-foreground mb-2 block">
                              Lösungsnotizen:
                            </label>
                            <textarea
                              value={resolutionNotes}
                              onChange={(e) => setResolutionNotes(e?.target?.value)}
                              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
                              rows={3}
                              placeholder="Beschreiben Sie, wie diese Warnung gelöst wurde..."
                            />
                            <div className="flex gap-2 mt-2">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleStatusChange(alert?.id, 'resolved')}
                              >
                                Als gelöst markieren
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setResolvingAlert(null);
                                  setResolutionNotes('');
                                }}
                              >
                                Abbrechen
                              </Button>
                            </div>
                          </div>
                        )}

                        {alert?.resolvedBy && alert?.resolvedAt && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-xs text-green-700">
                              Gelöst von {alert?.resolvedBy} am {formatDate(alert?.resolvedAt)}
                            </p>
                            {alert?.resolutionNotes && (
                              <p className="text-sm text-green-900 mt-1">{alert?.resolutionNotes}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityAlertsPanel;